import { t } from '@lingui/core/macro';
import {
    OfferSelectionMetadataDTO,
    PriceType,
    SolutionPreferenceDTO,
    SourcingPreferenceOptions,
} from '@luminovo/api-contracts';
import { assertPresent, isPresent } from '@luminovo/commons';
import { colorSystem, Flexbox, IconElement, iconSlot, StatusIcon, Tag, Text, Tooltip } from '@luminovo/design-system';
import { formatLongDateTime, formatRelativeTime, formatToLongDate, transEnum } from '@luminovo/frontend-utils';
import { Box, styled } from '@mui/material';
import { CalendarDaysIcon, ClockIcon, DollarSignIcon, PackageIcon, TagIcon, TrendingDownIcon } from 'lucide-react';
import React from 'react';

import { packagingTranslations, priceTypeTranslations, sourcingPreferenceTranslations } from '../../i18n';

interface SolutionPreferencesDisplayProps {
    solutionPreference: SolutionPreferenceDTO;
}

export function getLeadTimeLabel(leadTimePreference: SolutionPreferenceDTO['lead_time_preference']): {
    label: string;
    icon: IconElement;
} {
    switch (leadTimePreference.type) {
        case SourcingPreferenceOptions.Fastest:
            return {
                label: transEnum(SourcingPreferenceOptions.Fastest, sourcingPreferenceTranslations),
                icon: <ClockIcon />,
            };
        case SourcingPreferenceOptions.BestPrice:
            return {
                label: transEnum(SourcingPreferenceOptions.BestPrice, sourcingPreferenceTranslations),
                icon: <TrendingDownIcon />,
            };
        case SourcingPreferenceOptions.BestPriceBy:
            return {
                label: t`Best price by ${formatToLongDate(assertPresent(leadTimePreference.target))}`,
                icon: <TrendingDownIcon />,
            };
        default:
            return { label: '', icon: <TrendingDownIcon /> };
    }
}

export function getIncludedPriceTypesLabel(excludedPriceTypes: PriceType[]): string {
    const allPriceTypes = Object.values(PriceType);
    const includedPriceTypes = allPriceTypes.filter((pt) => !excludedPriceTypes.includes(pt));

    if (includedPriceTypes.length === allPriceTypes.length) {
        return t`All price types`;
    }

    if (includedPriceTypes.length === 0) {
        return t`No price types`;
    }

    const translatedTypes = includedPriceTypes.map((pt) => transEnum(pt, priceTypeTranslations));
    return translatedTypes.join(', ');
}

export function getPackagingLabel(
    packagingPreference: SolutionPreferenceDTO['packaging_preference'],
): string | undefined {
    if (packagingPreference.length === 0) {
        return undefined;
    }
    const translatedPackages = packagingPreference.map((p) => transEnum(p, packagingTranslations));
    return t`Prefer ${translatedPackages.join(', ')}`;
}

export function getValidationWindowLabel(
    offerValidationWindow: SolutionPreferenceDTO['offer_validation_window'],
): string | undefined {
    if (!isPresent(offerValidationWindow)) {
        return undefined;
    }
    const start = formatToLongDate(offerValidationWindow.start);
    const end = formatToLongDate(offerValidationWindow.end);
    return t`${start} – ${end}`;
}

export function getPriceComparisonLabel(useUnitPrice: boolean): string {
    return useUnitPrice ? t`Unit price` : t`Total purchase price`;
}

interface PreferenceTagProps {
    /** Pass it without a size or color: the tag draws it at 14px in neutral[7]. */
    icon?: IconElement;
    label: string;
    tooltip?: string;
}

function OfferSelectionInfoBox({ offerSelectionText }: { offerSelectionText: string }): JSX.Element {
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                height: 32,
                minHeight: 32,
                px: 0,
                py: 0,
                boxSizing: 'border-box',
            }}
        >
            <StatusIcon status="info" variant="filled" size={16} style={{ flexShrink: 0 }} />
            <Text variant="body-small" color={colorSystem.neutral[7]}>
                {offerSelectionText}
            </Text>
        </Box>
    );
}

export function OfferSelectionRightSection({
    offerSelectionMetadata,
    loadApiOffersSlot,
    runSelectionSlot,
}: {
    offerSelectionMetadata?: OfferSelectionMetadataDTO;
    loadApiOffersSlot?: React.ReactNode;
    runSelectionSlot?: React.ReactNode;
}): JSX.Element | null {
    if (!isPresent(loadApiOffersSlot) && !isPresent(runSelectionSlot)) {
        return null;
    }
    const hasLastSelection = isPresent(offerSelectionMetadata?.last_offer_selection_time);
    const offerSelectionText = hasLastSelection
        ? t`Offer selection ran ${formatRelativeTime(offerSelectionMetadata!.last_offer_selection_time)}`
        : t`No offer selection run yet`;
    const offerSelectionTooltip = hasLastSelection ? (
        <Flexbox
            gap={4}
            sx={{
                flexDirection: 'column',
            }}
        >
            <Text variant="body-small-semibold" color={colorSystem.neutral[1]}>
                {t`Last offer selection run at: ${formatLongDateTime(offerSelectionMetadata!.last_offer_selection_time)}`}
            </Text>
            {offerSelectionMetadata!.last_offer_selected_by && (
                <Text variant="body-small" color={colorSystem.neutral[4]}>
                    {t`by ${offerSelectionMetadata!.last_offer_selected_by.first_name} ${offerSelectionMetadata!.last_offer_selected_by.last_name}`}
                </Text>
            )}
        </Flexbox>
    ) : undefined;

    return (
        <Flexbox
            gap={8}
            sx={{
                alignItems: 'center',
            }}
        >
            {hasLastSelection ? (
                <Tooltip title={offerSelectionTooltip!}>
                    <span>
                        <OfferSelectionInfoBox offerSelectionText={offerSelectionText} />
                    </span>
                </Tooltip>
            ) : (
                <OfferSelectionInfoBox offerSelectionText={offerSelectionText} />
            )}
            {loadApiOffersSlot}
            {runSelectionSlot}
        </Flexbox>
    );
}

const PreferenceTagLabel = styled(Flexbox)({
    alignItems: 'center',
    ...iconSlot(14),
    '& > svg': {
        color: colorSystem.neutral[7],
    },
});

function PreferenceTag({ icon, label, tooltip }: PreferenceTagProps): JSX.Element {
    const tag = (
        <Tag
            color="neutral"
            attention="low"
            label={
                <PreferenceTagLabel gap={4}>
                    {icon}
                    <Text variant="body-small">{label}</Text>
                </PreferenceTagLabel>
            }
        />
    );

    if (tooltip) {
        return <Tooltip title={tooltip}>{tag}</Tooltip>;
    }

    return tag;
}

export function SolutionPreferencesDisplay({ solutionPreference }: SolutionPreferencesDisplayProps): JSX.Element {
    const leadTimePreference = getLeadTimeLabel(solutionPreference.lead_time_preference);
    const packagingLabel = getPackagingLabel(solutionPreference.packaging_preference);
    const priceTypesLabel = getIncludedPriceTypesLabel(solutionPreference.excluded_price_types);
    const validationWindowLabel = getValidationWindowLabel(solutionPreference.offer_validation_window);
    const priceComparisonLabel = getPriceComparisonLabel(solutionPreference.use_unit_price);

    return (
        <Flexbox
            style={{ flex: 1, width: '100%' }}
            sx={{
                alignItems: 'center',
                flexWrap: 'wrap',
            }}
        >
            {/* Left group: tags + Edit */}
            <Flexbox
                gap={4}
                sx={{
                    alignItems: 'center',
                    flexWrap: 'wrap',
                }}
            >
                <PreferenceTag
                    icon={leadTimePreference.icon}
                    label={leadTimePreference.label}
                    tooltip={t`Lead time preference`}
                />
                <PreferenceTag
                    icon={<DollarSignIcon />}
                    label={priceComparisonLabel}
                    tooltip={t`Price comparison basis`}
                />
                {packagingLabel && (
                    <PreferenceTag icon={<PackageIcon />} label={packagingLabel} tooltip={t`Packaging preference`} />
                )}
                <PreferenceTag icon={<TagIcon />} label={priceTypesLabel} tooltip={t`Included price types`} />
                {validationWindowLabel && (
                    <PreferenceTag
                        icon={<CalendarDaysIcon />}
                        label={validationWindowLabel}
                        tooltip={t`Offer validation window`}
                    />
                )}
            </Flexbox>
        </Flexbox>
    );
}
